/**
 * useInboundRouting Hook
 *
 * Manages inbound routing rules state with TanStack Query for server state.
 * Inbound routing determines which PROJECT incoming records are routed to.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { useToast } from '@/hooks/use-toast';
import { inboundRoutingKeys } from '@/lib/query-options';
import {
  isPriorityTaken,
  isReorderConflict,
} from '@/features/routing/utils/routing-errors';

/**
 * Options of the rule create/update hooks. `priorityTakenAtField` — the caller
 * shows the PRIORITY_TAKEN refusal (#2949) at its priority field, so the hook
 * adds no failure toast for it; every other caller keeps the toast.
 */
interface RuleWriteOptions {
  priorityTakenAtField?: boolean;
}
import type {
  InboundRoutingRule,
  InboundRoutingListResponse,
  CreateInboundRoutingRuleRequest,
  UpdateInboundRoutingRuleRequest,
  InboundRoutingFallbackSettings,
  UpdateInboundRoutingFallbackRequest,
  InboundRoutingReorderResponse,
} from '@/lib/api/types';

interface UseInboundRoutingRulesOptions {
  projectId?: string;
  enabled?: boolean;
}

/**
 * Hook for fetching inbound routing rules list
 */
export function useInboundRoutingRules(options: UseInboundRoutingRulesOptions = {}) {
  const { projectId, enabled = true } = options;
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: inboundRoutingKeys.list(selectedOrgId, { projectId }),
    queryFn: () => apiClient.inboundRouting.getInboundRoutingRules({ projectId }),
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === selectedOrgId ? previousData : undefined,
  });
}

/**
 * Hook for fetching single inbound routing rule
 */
export function useInboundRoutingRule(id: string, enabled = true) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: inboundRoutingKeys.detail(selectedOrgId, id),
    queryFn: () => apiClient.inboundRouting.getInboundRoutingRuleById(id),
    enabled: enabled && !!id && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching organization fallback settings
 */
export function useInboundRoutingFallback(enabled = true) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: inboundRoutingKeys.fallback(selectedOrgId),
    queryFn: () => apiClient.inboundRouting.getFallbackSettings(),
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for creating inbound routing rule
 */
export function useCreateInboundRoutingRule({
  priorityTakenAtField = false,
}: RuleWriteOptions = {}) {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<InboundRoutingRule, Error, CreateInboundRoutingRuleRequest>({
    mutationFn: (data: CreateInboundRoutingRuleRequest) =>
      apiClient.inboundRouting.createInboundRoutingRule(data),
    onSuccess: (newRule: InboundRoutingRule) => {
      queryClient.setQueryData(
        inboundRoutingKeys.detail(selectedOrgId, newRule.id),
        newRule
      );
      // Keep create path as invalidate because list membership depends on active filters.
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      toast({
        title: t('routing.toast.routingRuleCreated', 'Routing rule created'),
        description: t(
          'routing.toast.ruleCreatedDescription',
          'Rule "{{name}}" has been created.'
        ).replace('{{name}}', newRule.name),
      });
    },
    onError: (error: Error) => {
      if (priorityTakenAtField && isPriorityTaken(error)) return;
      toast({
        title: t(
          'routing.toast.routingRuleCreateFailed',
          'Failed to create routing rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for updating inbound routing rule
 */
export function useUpdateInboundRoutingRule({
  priorityTakenAtField = false,
}: RuleWriteOptions = {}) {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<
    InboundRoutingRule,
    Error,
    { id: string; data: UpdateInboundRoutingRuleRequest }
  >({
    mutationFn: ({ id, data }: { id: string; data: UpdateInboundRoutingRuleRequest }) =>
      apiClient.inboundRouting.updateInboundRoutingRule(id, data),
    onSuccess: (updatedRule: InboundRoutingRule) => {
      queryClient.setQueryData(
        inboundRoutingKeys.detail(selectedOrgId, updatedRule.id),
        updatedRule
      );
      queryClient.setQueriesData<InboundRoutingListResponse>(
        { queryKey: inboundRoutingKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          let changed = false;
          const nextRules = previous.data.map((rule) => {
            if (rule.id !== updatedRule.id) return rule;
            changed = true;
            return updatedRule;
          });
          return changed ? { ...previous, data: nextRules } : previous;
        }
      );
      // The patch above cannot know `meta.enabledTotal` (enabled rules across
      // all pages): refetch so the count is the server's, not a stale one.
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      toast({
        title: t('routing.toast.routingRuleUpdated', 'Routing rule updated'),
        description: t(
          'routing.toast.ruleUpdatedDescription',
          'Rule "{{name}}" has been updated.'
        ).replace('{{name}}', updatedRule.name),
      });
    },
    onError: (error: Error) => {
      if (priorityTakenAtField && isPriorityTaken(error)) return;
      toast({
        title: t(
          'routing.toast.routingRuleUpdateFailed',
          'Failed to update routing rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for deleting inbound routing rule
 */
export function useDeleteInboundRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id: string) => apiClient.inboundRouting.deleteInboundRoutingRule(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueriesData<InboundRoutingListResponse>(
        { queryKey: inboundRoutingKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          const nextRules = previous.data.filter((rule) => rule.id !== deletedId);
          if (nextRules.length === previous.data.length) return previous;
          return {
            ...previous,
            data: nextRules,
            meta: { ...previous.meta, total: Math.max(0, previous.meta.total - 1) },
          };
        }
      );
      // The patch above cannot know `meta.enabledTotal` (enabled rules across
      // all pages): refetch so the count is the server's, not a stale one.
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      queryClient.removeQueries({
        queryKey: inboundRoutingKeys.detail(selectedOrgId, deletedId),
        exact: true,
      });
      toast({
        title: t('routing.toast.routingRuleDeleted', 'Routing rule deleted'),
        description: t(
          'routing.toast.routingRuleDeletedDescription',
          'The routing rule has been deleted.'
        ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.routingRuleDeleteFailed',
          'Failed to delete routing rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for toggling inbound routing rule enabled status
 */
export function useToggleInboundRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<InboundRoutingRule, Error, string>({
    mutationFn: (id: string) => apiClient.inboundRouting.toggleInboundRoutingRule(id),
    onSuccess: (updatedRule: InboundRoutingRule) => {
      queryClient.setQueryData(
        inboundRoutingKeys.detail(selectedOrgId, updatedRule.id),
        updatedRule
      );
      queryClient.setQueriesData<InboundRoutingListResponse>(
        { queryKey: inboundRoutingKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          let changed = false;
          const nextRules = previous.data.map((rule) => {
            if (rule.id !== updatedRule.id) return rule;
            changed = true;
            return updatedRule;
          });
          return changed ? { ...previous, data: nextRules } : previous;
        }
      );
      // The patch above cannot know `meta.enabledTotal` (enabled rules across
      // all pages): refetch so the count is the server's, not a stale one.
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      toast({
        title: updatedRule.isEnabled
          ? t('routing.toast.ruleEnabled', 'Rule enabled')
          : t('routing.toast.ruleDisabled', 'Rule disabled'),
        description: t(
          'routing.toast.ruleToggleDescription',
          'Rule "{{name}}" is now {{status}}.'
        )
          .replace('{{name}}', updatedRule.name)
          .replace(
            '{{status}}',
            updatedRule.isEnabled
              ? t('routing.toast.enabled', 'enabled')
              : t('routing.toast.disabled', 'disabled')
          ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.routingRuleToggleFailed',
          'Failed to toggle routing rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for reordering inbound routing rules (globally within org)
 */
export function useReorderInboundRoutingRules() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<InboundRoutingReorderResponse, Error, { ruleIds: string[] }>({
    mutationFn: ({ ruleIds }: { ruleIds: string[] }) =>
      apiClient.inboundRouting.reorderInboundRoutingRules({ ruleIds }),
    onSuccess: (_, variables) => {
      const orderMap = new Map(variables.ruleIds.map((id, index) => [id, index]));
      queryClient.setQueriesData<InboundRoutingListResponse>(
        { queryKey: inboundRoutingKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;

          const hasAnyUpdatedRule = previous.data.some((rule) => orderMap.has(rule.id));
          if (!hasAnyUpdatedRule) return previous;

          // The order the server sets (`InboundRoutingRepository.planReorder`):
          // the listed rules change places among themselves, every other rule
          // keeps its own — one project's tab must not move other projects'
          // rules. The priorities are only a guess; the refetch below brings
          // the server's.
          const byPriority = [...previous.data].sort((a, b) => a.priority - b.priority);
          const places = byPriority.filter((rule) => orderMap.has(rule.id));
          const reordered = [...places].sort(
            (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
          );
          const priorityOf = new Map(
            reordered.map((rule, index) => [
              rule.id,
              places[index]?.priority ?? rule.priority,
            ])
          );
          const nextRules = byPriority
            .map((rule) => {
              const priority = priorityOf.get(rule.id);
              return priority === undefined ? rule : { ...rule, priority };
            })
            .sort((a, b) => a.priority - b.priority);

          return { ...previous, data: nextRules };
        }
      );
      // The server may write other priorities than the patch above guesses —
      // gaps between the neighbours, or, with no room, a renumber of unlisted
      // rules too (#2851, #2873). Refetch, so an unlisted rule does not keep —
      // and an edit write back — a priority the server no longer has (#2867).
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.details(selectedOrgId),
      });
      toast({
        title: t('routing.toast.rulesReordered', 'Rules reordered'),
        description: t(
          'routing.toast.rulesReorderedDescription',
          'Routing rule priority has been updated.'
        ),
      });
    },
    onError: (error: Error) => {
      // A refused reorder writes nothing; drop whatever a screen guessed.
      void queryClient.invalidateQueries({
        queryKey: inboundRoutingKeys.lists(selectedOrgId),
      });
      toast({
        title: t('routing.toast.rulesReorderFailed', 'Failed to reorder rules'),
        description: isReorderConflict(error)
          ? t(
              'routing.toast.reorderConflict',
              'Routing rules changed while you were reordering. Nothing was saved — the list is up to date now, try again.'
            )
          : error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for updating fallback settings
 */
export function useUpdateInboundRoutingFallback() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<
    InboundRoutingFallbackSettings,
    Error,
    UpdateInboundRoutingFallbackRequest
  >({
    mutationFn: (data: UpdateInboundRoutingFallbackRequest) =>
      apiClient.inboundRouting.updateFallbackSettings(data),
    onSuccess: (data) => {
      // Immediately update cache with server response (prevents stale UI between mutation and refetch)
      queryClient.setQueryData(inboundRoutingKeys.fallback(selectedOrgId), data);
      toast({
        title: t('routing.toast.fallbackUpdated', 'Fallback settings updated'),
        description: t(
          'routing.toast.fallbackUpdatedDescription',
          'Organization fallback settings have been updated.'
        ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.fallbackUpdateFailed',
          'Failed to update fallback settings'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}
